import { ApiTransport } from './ApiTransport';
import type { AccessibleProject, ProjectMember, Role } from './types';
import { extractError } from './apiErrors';

export class AccessClient {
  constructor(private readonly transport: ApiTransport) {}

  async getMyRoles(): Promise<{ data: AccessibleProject[]; userId: string }> {
    const response = await this.transport.request(`/roles/my-roles`);
    if (!response.ok) return { data: [], userId: '' };
    return response.json();
  }

  async getProjectMembers(projectKey: string): Promise<ProjectMember[]> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/members`,
    );
    if (!response.ok) return [];
    const result = await response.json();
    return result.data;
  }

  async getAssignableRoles(projectKey: string): Promise<Role[]> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/assignable-roles`,
    );
    if (!response.ok) return [];
    const result = await response.json();
    return result.data;
  }

  async addProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    expiresAt?: string,
    repositoryKeys?: string[],
  ): Promise<ProjectMember> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/members`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, role, expiresAt, repositoryKeys }),
      },
    );
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to add member');
      throw new Error(msg);
    }
    return response.json();
  }

  async removeProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    repositoryKey?: string | null,
  ): Promise<void> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/members`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
          role,
          repositoryKey: repositoryKey ?? null,
        }),
      },
    );
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to remove member');
      throw new Error(msg);
    }
  }

  async updateProjectMemberRole(
    projectKey: string,
    entityRef: string,
    oldRole: Role,
    newRole: Role,
  ): Promise<ProjectMember> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/members`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, oldRole, newRole }),
      },
    );
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to update role');
      throw new Error(msg);
    }
    return response.json();
  }

  async renewProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    newExpiresAt: string,
  ): Promise<ProjectMember> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/members/renew`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, role, newExpiresAt }),
      },
    );
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to renew member');
      throw new Error(msg);
    }
    return response.json();
  }

  async invalidateRbacCache(
    userEntityRef?: string,
  ): Promise<{ invalidated: string }> {
    const response = await this.transport.request(`/roles/cache/invalidate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: userEntityRef ? JSON.stringify({ userEntityRef }) : '{}',
    });
    if (!response.ok) {
      const msg = await extractError(
        response,
        'Failed to invalidate RBAC cache',
      );
      throw new Error(msg);
    }
    return response.json();
  }
}
